import type { ActorRefFrom } from 'xstate';
import { assign, setup } from 'xstate';
import type { EmployerFacilityValues } from '~/features/market/facility/types';
import { toStoredFacilityName } from '~/features/market/facility/utils';
import { TypeAndCategoryValues } from '~/features/provider/registration-v2/types';
import type { FacilityFormValues } from '~/features/types';
import { Role } from '~/features/user/types';
import { facilityMachine } from '~/features/vault';
import {
  ALERT_VIEW_STATE,
  EMPLOYER_REGISTRATION_SERVICE,
  EMPLOYER_REGISTRATION_VIEW,
} from '~/features/xstateSystem';
import { employerSignupSkipsApproval } from './approval';
import { employerRegistrationService } from './registration.service.machine';
import type { EmployerInfoValues, WelcomeValues } from './types';

export const employerRegistrationView = setup({
  actors: {
    employerRegistrationService: employerRegistrationService,
    facilityRef: facilityMachine,
  },
  guards: {
    skipsApproval: () => employerSignupSkipsApproval(),
  },
  types: {
    context: {} as {
      registrationService?: ActorRefFrom<typeof employerRegistrationService>;
      facilityRef: ActorRefFrom<typeof facilityMachine>;
      userInfoId: number;
      loading: boolean;
      welcomeForm: WelcomeValues;
      employerInfo: EmployerInfoValues;
      employerFacility: EmployerFacilityValues;
      route: string;
      registered: boolean;
      employerUserId?: number;
      employerId?: number;
      typeAndCategoryForm: TypeAndCategoryValues;
    },
    events: {} as
      | { type: 'ON_NEXT_PAGE'; goTo: string; payload: WelcomeValues | undefined }
      | { type: 'ON_SKIP' }
      | { type: 'ON_SET_PAGE'; goTo: string }
      | { type: 'ON_OPEN_MODAL' }
      | { type: 'ON_CLOSE_MODAL' }
      | { type: 'ON_RESET_ERROR' }
      | { type: 'ON_REGISTRATION_SUBMIT' }
      | { type: 'ON_SET_LOADING'; payload: { loading: boolean } }
      | { type: 'ON_SUBMIT_WELCOME'; data: WelcomeValues }
      | { type: 'ON_SUBMIT_EMPLOYER_INFO'; data: EmployerInfoValues }
      | { type: 'ON_SUBMIT_EMPLOYER_FACILITY'; data: EmployerFacilityValues }
      | { type: 'ON_OPEN_FACILITY_FORM' }
      | { type: 'ON_CLOSE_FACILITY_FORM' }
      | { type: 'ON_FACILITY_CREATED'; facility: FacilityFormValues }
      | { type: 'ON_COMPLETE_REGISTRATION' }
      | { type: 'ON_PREVIOUS_PAGE'; draft?: EmployerFacilityValues }
      | { type: 'ON_BYPASS_APPROVAL' }
      // Events from service (via subscription)
      | { type: 'ON_EMPLOYER_AND_USER_CREATED'; employerUserId: number; employerId: number }
      | { type: 'ON_SERVICE_ERROR'; error: Error }
      | { type: 'ON_REGISTRATION_COMPLETE' },
    output: {} as {
      providerId?: number;
      employerUserId?: number;
      employerId?: number;
      roleCode?: Role;
    },
    input: {} as {
      userInfoId: number;
      primaryEmail: string;
    },
  },
}).createMachine({
  id: EMPLOYER_REGISTRATION_VIEW,
  initial: '$_WELCOME',
  // [MCV-1937] What the user machine's $_EMPLOYER_REGISTRATION onDone receives. It must be
  // the ROOT output: XState 5 ignores an `output` on a top-level final state unless the root
  // maps it, so on $_COMPLETED it left the new employer with no roleCode or employerUserId.
  output: ({ context }) => {
    if (!context.registrationService) {
      throw new Error('registrationService is not initialized');
    }
    const { employerUserId, employerId } = context.registrationService.getSnapshot().context;

    return {
      providerId: undefined,
      employerUserId: employerUserId ?? undefined,
      employerId: employerId ?? undefined,
      roleCode: 'EMPLOYER' as const,
    };
  },
  context: ({ input, spawn }) => ({
    route: 'welcome',
    facilityRef: spawn('facilityRef', {
      id: 'facilityState',
      systemId: 'facilityState',
      input: undefined,
    }),
    userInfoId: input.userInfoId,
    registrationService: undefined as ActorRefFrom<typeof employerRegistrationService> | undefined,
    loading: false,
    registered: false,
    welcomeForm: {
      email: input.primaryEmail,
      phone: undefined,
      mfa: false,
      tosModalOpen: false,
      tosAccepted: false,
    },
    employerInfo: {
      legalEntityName: '',
      employerEin: '',
      employerNpi: '',
      employerType: { label: '', value: '' },
      ownershipStructure: { label: '', value: '' },
      employerWebsite: '',
    },
    employerFacility: {
      facilityName: { label: '', value: '' },
      facilityNpi: '',
      clinicalSettingCategory: { label: '', value: '' },
      clinicalSetting: { label: '', value: '' },
      facilitySize: { label: '', value: '' },
      areaType: { label: '', value: '' },
      facilityId: undefined,
    },
    typeAndCategoryForm: {
      category: null,
      type: null,
      firstName: '',
      lastName: '',
      npiNum: '',
    },
  }),
  states: {
    $_WELCOME: {
      entry: assign({
        // [MCV-1667] Re-entering $_WELCOME on Back from $_EMPLOYER_INFO must
        // not spawn a second registrationService -- the first one is still
        // alive and subscribed. Reuse it if one already exists.
        registrationService: ({ spawn, self, context }) => {
          if (context.registrationService) return context.registrationService;
          return spawn(EMPLOYER_REGISTRATION_SERVICE, {
            input: {
              userInfoId: context.userInfoId,
              parentRef: self,
            },
          });
        },
      }),
      on: {
        ON_OPEN_MODAL: {
          actions: assign({
            welcomeForm: ({ context }) => ({ ...context.welcomeForm, tosModalOpen: true }),
          }),
        },
        ON_CLOSE_MODAL: {
          actions: assign({
            welcomeForm: ({ context }) => ({ ...context.welcomeForm, tosModalOpen: false }),
          }),
        },
        ON_SUBMIT_WELCOME: {
          actions: [
            assign({
              welcomeForm: ({ event }) =>
                (event as { type: 'ON_SUBMIT_WELCOME'; data: WelcomeValues }).data,
            }),
          ],
          target: '$_EMPLOYER_INFO',
        },
      },
    },
    $_EMPLOYER_INFO: {
      on: {
        ON_SUBMIT_EMPLOYER_INFO: {
          actions: assign({
            employerInfo: ({ event }) => event.data,
          }),
          target: '$_EMPLOYER_FACILITY',
        },
        // [MCV-1667] Restores the Footer's Back control on page 2, which was
        // previously unwired because $_EMPLOYER_INFO had no ON_PREVIOUS_PAGE
        // handler at all.
        ON_PREVIOUS_PAGE: {
          target: '$_WELCOME',
        },
      },
    },
    $_EMPLOYER_FACILITY: {
      on: {
        ON_FACILITY_CREATED: {
          actions: assign({
            employerFacility: ({ context, event }) => ({
              ...context.employerFacility,
              // [MCV-1876] Store the facility's own name, not the picker label with its suffix.
              facilityName: {
                label: toStoredFacilityName(event.facility),
                value: toStoredFacilityName(event.facility),
              },
              facilityId: event.facility.value,
            }),
          }),
        },
        // [MCV-1957] Prod creates the employer on submit; elsewhere the pending screen's
        // Bypass Approval button does. On a failed create both land on $_PENDING_VERIFICATION,
        // which waits for a click, so a failure is never retried in a loop.
        ON_SUBMIT_EMPLOYER_FACILITY: [
          {
            guard: 'skipsApproval',
            actions: assign({ employerFacility: ({ event }) => event.data }),
            target: '$_BYPASSING',
          },
          {
            actions: assign({ employerFacility: ({ event }) => event.data }),
            target: '$_PENDING_VERIFICATION',
          },
        ],
        // [MCV-1667] Restores the Footer's Back control on page 3. An
        // optional `draft` carries the in-progress form values (the facility
        // step is never submitted via ON_SUBMIT_EMPLOYER_FACILITY on Back),
        // so re-entering this step keeps what was typed instead of the
        // stale/empty context.employerFacility.
        ON_PREVIOUS_PAGE: {
          actions: assign({
            employerFacility: ({ context, event }) => event.draft ?? context.employerFacility,
          }),
          target: '$_EMPLOYER_INFO',
        },
      },
    },
    $_PENDING_VERIFICATION: {
      on: {
        ON_BYPASS_APPROVAL: {
          target: '$_BYPASSING',
        },
        ON_REGISTRATION_COMPLETE: {
          target: '$_COMPLETED',
        },
        ON_SERVICE_ERROR: {
          actions: ({ system }) => {
            try {
              system?.get(ALERT_VIEW_STATE).send({
                type: 'ON_SHOW_ALERT',
                payload: {
                  text: 'Registration failed',
                  subText: 'Please try again.',
                  type: 'error',
                  timeout: 5000,
                },
              });
            } catch (error) {
              console.error('[VIEW] Failed to send alert:', error);
            }
          },
        },
      },
    },
    $_BYPASSING: {
      entry: [
        assign({ loading: true }),
        ({ context }) => {
          context.registrationService?.send({
            type: 'BYPASS_APPROVAL',
            data: {
              userInfoId: context.userInfoId,
              email: context.welcomeForm.email,
              tosAccepted: context.welcomeForm.tosAccepted,
              legalEntityName: context.employerInfo.legalEntityName,
              employerEin: context.employerInfo.employerEin || undefined,
              employerNpi: context.employerInfo.employerNpi || undefined,
              employerType: context.employerInfo.employerType.value,
              ownershipStructure: context.employerInfo.ownershipStructure.value,
              employerWebsite: context.employerInfo.employerWebsite,
              facilityName: context.employerFacility.facilityName.value,
              facilityId: context.employerFacility.facilityId,
              facilityNpi: context.employerFacility.facilityNpi || undefined,
              clinicalSettingCategory: context.employerFacility.clinicalSettingCategory.value,
              clinicalSetting: context.employerFacility.clinicalSetting.value,
              facilitySize: context.employerFacility.facilitySize.value,
              areaType: context.employerFacility.areaType.value,
            },
          });
        },
      ],
      on: {
        ON_REGISTRATION_COMPLETE: {
          target: '$_COMPLETED',
          actions: assign({ loading: false }),
        },
        ON_SERVICE_ERROR: {
          target: '$_PENDING_VERIFICATION',
          actions: [
            assign({ loading: false }),
            ({ system }) => {
              try {
                system?.get(ALERT_VIEW_STATE).send({
                  type: 'ON_SHOW_ALERT',
                  payload: {
                    text: 'We could not create your account',
                    subText: 'Please try again or contact support.',
                    type: 'error',
                    timeout: 5000,
                  },
                });
              } catch (error) {
                console.error('[VIEW] Failed to send alert:', error);
              }
            },
          ],
        },
      },
    },
    $_CREATING_EMPLOYER_AND_USER: {
      entry: ({ context }) => {
        // context.registrationService!.send({
        //   type: 'CREATE_PROVIDER',
        //   data: {
        //     typeAndCategory: context.typeAndCategoryForm,
        //     welcome: context.welcomeForm,
        //   }
        // });
      },
      on: {
        ON_EMPLOYER_CREATED: {
          target: '$_PENDING_VERIFICATION',
          actions: [
            assign({
              providerId: ({ event }) => (event as { type: string; providerId: number }).providerId,
            }),
          ],
        },
        ON_SERVICE_ERROR: {
          target: '$_PENDING_VERIFICATION',
          actions: [
            ({ system }) => {
              try {
                system?.get(ALERT_VIEW_STATE).send({
                  type: 'ON_SHOW_ALERT',
                  payload: {
                    text: 'Failed to create provider',
                    subText: 'Please try again or contact support if the problem persists.',
                    type: 'error',
                    timeout: 5000,
                  },
                });
              } catch (error) {
                console.error('[VIEW] Failed to send alert:', error);
              }
            },
          ],
        },
      },
    },
    $_COMPLETING: {
      on: {
        ON_REGISTRATION_COMPLETE: {
          target: '$_COMPLETED',
        },
        ON_SERVICE_ERROR: {
          target: '$_PENDING_VERIFICATION',
          actions: [
            ({ system }) => {
              try {
                system?.get(ALERT_VIEW_STATE)?.send({
                  type: 'ON_SHOW_ALERT',
                  payload: {
                    text: 'Registration completion failed',
                    subText: 'Please try again.',
                    type: 'error',
                    timeout: 5000,
                  },
                });
              } catch (error) {
                console.error('[VIEW] Failed to send alert:', error);
              }
            },
          ],
        },
      },
    },
    $_COMPLETED: {
      type: 'final' as const,
    },
  },
});
